import { ReactElement, useContext, useState } from "react";
import { Box } from "@justworkshr/milo-core";
import FactorHeader from "../factorHeader/FactorHeader";
import FactorSetup from "../factorSetup/FactorSetup";

import { Factor, FactorKey } from "types/factor/factor";
import { OktaError } from "types/oktaError/oktaError";
import {
  AuthTransaction,
  StatusEnum,
} from "pages/register/types/authTransaction";

import TotpQrCodeContext from "../../contexts/TotpQrCodeContext";
import AuthTransactionContext from "pages/register/contexts/AuthTransactionContext";
import DisableFactorSetupContext from "../../contexts/DisableFactorSetupContext";

import useCancelMfaEnrollment from "pages/register/hooks/useCancelMfaEnrollment";

const formatErrorResponse = (errorResponse: OktaError) => {
  return (
    errorResponse.errorSummary +
    ": " +
    errorResponse.errorCauses.map((error) => error.errorSummary).join(" | ")
  );
};

const getDefaultOpenState = (
  transaction: AuthTransaction | undefined,
  factor: Factor,
) => {
  let defaultOpenState = false;
  if (
    transaction &&
    transaction.status === StatusEnum.MfaEnrollActivate &&
    transaction.factor.factorType === factor.factorType &&
    transaction.factor.provider === factor.provider
  ) {
    defaultOpenState = true;
  }
  return defaultOpenState;
};

const MfaFactor = ({ factor }: { factor: Factor }): ReactElement => {
  const { requestEnrollTotp, authEnrollTotp, resetQrCodeData } =
    useContext(TotpQrCodeContext);
  const { transaction, setTransaction, factorData, setFactorData } = useContext(
    AuthTransactionContext,
  );
  const { setDisableFactorSetup } = useContext(DisableFactorSetupContext);
  const { mutate: cancelMfaEnrollment, isLoading: isCancellingAuthEnrollment } =
    useCancelMfaEnrollment();
  const isTotpFactor = factor.flags.isTotpFactor;
  const provider = factor.provider;

  const [isOpen, setIsOpen] = useState(
    getDefaultOpenState(transaction, factor),
  );

  const setOpenState = (isOpen: boolean) => {
    setIsOpen(isOpen);
    if (transaction) {
      setDisableFactorSetup(isOpen);
    }

    if (!isTotpFactor) return;

    if (!isOpen) {
      resetQrCodeData();
    } else if (isOpen && !transaction) {
      requestEnrollTotp({ provider });
    } else if (
      isOpen &&
      transaction &&
      transaction.status === StatusEnum.MfaEnroll
    ) {
      authEnrollTotp(
        {
          stateToken: transaction.data.stateToken,
          provider: provider,
        },
        {
          onSuccess: setTransaction,
          onError: (errorResponse) => {
            const errors = formatErrorResponse(errorResponse);
            console.log(errors); //temporary
          },
        },
      );
    }
  };

  const closeHandler = () => {
    if (transaction && transaction.status === StatusEnum.MfaEnrollActivate) {
      cancelMfaEnrollment(
        { stateToken: transaction.data.stateToken },
        {
          onSuccess: (transaction) => {
            setTransaction(transaction);
            setOpenState(false);
          },
          onError: (errorResponse) => {
            const errors = formatErrorResponse(errorResponse);
            console.log(errors); //temporary
          },
        },
      );
    } else {
      setOpenState(false);
    }
  };

  const handleWebAuthnSuccess = () => {
    const existingWebAuthn = factorData.factorMap[FactorKey.WebAuthn];
    if (!existingWebAuthn) return;
    setFactorData({
      ...factorData,
      factorMap: {
        ...factorData.factorMap,
        [FactorKey.WebAuthn]: {
          ...existingWebAuthn,
          flags: {
            ...existingWebAuthn.flags,
            isActive: true,
          },
        },
      },
    });
    setOpenState(false);
  };

  return (
    <Box
      border={{
        borderColor: "neutralSubtle",
        borderWidth: "sm",
        borderRadius: "md",
      }}
      padding="md"
    >
      <FactorHeader
        factor={factor}
        onClickSetup={() => setOpenState(true)}
        isSetUpOpen={isOpen}
      />

      {isOpen && (
        <FactorSetup
          factor={factor}
          onCancel={closeHandler}
          onSuccess={
            factor.key === FactorKey.WebAuthn
              ? handleWebAuthnSuccess
              : undefined
          }
          isCancellingAuthEnrollment={isCancellingAuthEnrollment}
        />
      )}
    </Box>
  );
};

export default MfaFactor;
